import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ImageUp, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Logo de l'établissement, repris automatiquement sur bulletins et reçus
export const TenantLogoUploader = ({ initialUrl }) => {
  const [logoUrl, setLogoUrl] = useState(initialUrl || null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) return toast.error('Formats acceptés : PNG, JPG, WEBP');
    if (file.size > 2 * 1024 * 1024) return toast.error('2 Mo maximum');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${BACKEND_URL}/api/tenant/logo`, fd);
      setLogoUrl(data.logo_url);
      window.dispatchEvent(new Event('tenant-logo-updated'));
      toast.success('Logo enregistré');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de l\'envoi du logo');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/tenant/logo`);
      setLogoUrl(null);
      window.dispatchEvent(new Event('tenant-logo-updated'));
      toast.success('Logo supprimé');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="card" data-testid="tenant-logo-card">
      <CardHeader>
        <CardTitle>Logo de l'établissement</CardTitle>
        <CardDescription>Utilisé sur le tableau de bord, les bulletins et les reçus de paiement.</CardDescription>
      </CardHeader>
      <CardContent className="flex items-center gap-6">
        <div className="w-28 h-28 rounded-lg border bg-gray-50 flex items-center justify-center overflow-hidden">
          {logoUrl ? <img src={`${BACKEND_URL}${logoUrl}`} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="tenant-logo-preview" />
            : <span className="text-xs text-gray-400">Aucun logo</span>}
        </div>
        <div className="flex gap-2">
          <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="tenant-logo-input"
            onChange={(e) => upload(e.target.files?.[0])} />
          <Button type="button" onClick={() => input.current?.click()} disabled={busy} data-testid="tenant-logo-upload-btn">
            <ImageUp className="w-4 h-4 mr-2" />{logoUrl ? 'Changer' : 'Ajouter'}
          </Button>
          {logoUrl && (
            <Button type="button" variant="outline" onClick={remove} disabled={busy} data-testid="tenant-logo-delete-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
